"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.not = exports.where = exports.CriteriaBuilder = void 0;
const and_operation_1 = require("../expression-tree/nodes/and-operation");
const binary_operation_1 = require("../expression-tree/nodes/binary-operation");
const expression_node_1 = require("../expression-tree/nodes/expression-node");
const no_op_1 = require("../expression-tree/nodes/no-op");
const not_operation_1 = require("../expression-tree/nodes/not-operation");
const or_operation_1 = require("../expression-tree/nodes/or-operation");
const sub_expression_1 = require("../expression-tree/nodes/sub-expression");
const unary_operation_1 = require("../expression-tree/nodes/unary-operation");
const create_predicate_1 = require("./create-predicate");
/**
 * Provides an infix-like API for dynamically building a criteria expression (represented by a tree of {@link ExpressionNode} instances).
 */
class CriteriaBuilder {
    constructor(...unionParam) {
        this[Symbol.toStringTag] = 'CriteriaBuilder';
        this.root = CriteriaBuilder.extrapolateExpression(CriteriaBuilder.normalizeOperand(unionParam));
    }
    get expressionTree() {
        return this.root;
    }
    static where(...unionParam) {
        const operand = CriteriaBuilder.normalizeOperand(unionParam);
        const expression = CriteriaBuilder.extrapolateExpression(operand);
        const precedenceAwareExpression = CriteriaBuilder.isNonEmptyCriteriaBuilder(operand) ? new sub_expression_1.SubExpression(expression) : expression;
        return new CriteriaBuilder(precedenceAwareExpression);
    }
    static not(...unionParam) {
        const expression = CriteriaBuilder.extrapolateExpression(CriteriaBuilder.normalizeOperand(unionParam));
        const negatedExpression = expression instanceof no_op_1.NoOp ? expression : new not_operation_1.NotOperation(expression);
        return new CriteriaBuilder(negatedExpression);
    }
    static isNonEmptyCriteriaBuilder(subject) {
        return subject instanceof CriteriaBuilder && !subject.isEmpty();
    }
    static normalizeOperand(operand) {
        return operand.length === 3 ? operand : operand[0];
    }
    /**
     * From each common operand type, there is either an explicit `ExpressionNode` or one that can be created. Here we analyze the operand
     * type and extrapolate from it some `ExpressionNode` that it represents.
     */
    static extrapolateExpression(operand) {
        if (operand === undefined) {
            return new no_op_1.NoOp();
        }
        else if (operand instanceof CriteriaBuilder) {
            return operand.root.clone();
        }
        else if (operand instanceof expression_node_1.ExpressionNode) {
            return operand;
        }
        else {
            return create_predicate_1.createPredicate(operand);
        }
    }
    and(...unionParam) {
        const rightOperand = CriteriaBuilder.extrapolateExpression(CriteriaBuilder.normalizeOperand(unionParam));
        // since we don't yet know *where* to apply this operation in the tree, we use `NoOp` as a placeholder
        this.applyBinaryOperation(new and_operation_1.AndOperation(new no_op_1.NoOp(), rightOperand));
        return this;
    }
    or(...unionParam) {
        const rightOperand = CriteriaBuilder.extrapolateExpression(CriteriaBuilder.normalizeOperand(unionParam));
        this.applyBinaryOperation(new or_operation_1.OrOperation(new no_op_1.NoOp(), rightOperand));
        return this;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    evaluate(operand) {
        return this.root.evaluate(operand);
    }
    applyBinaryOperation(operation) {
        if (operation.rightOperand instanceof no_op_1.NoOp) {
            // we were given an "empty" operation. do nothing
            // e.g. `where(1, '==', 1).and(new CriteriaBuilder());`
        }
        else if (this.root instanceof no_op_1.NoOp) {
            // the current builder is empty, so simply replace the current tree with the new operand
            // e.g. `new CriteriaBuilder().and(1, '==', 1);`
            this.root = operation.rightOperand;
        }
        else {
            this.insertBinaryOperation(operation);
        }
    }
    insertBinaryOperation(operation) {
        const parent = this.findParentOfNodeWithHigherPrecedence(operation.precedence);
        if (parent === null) {
            this.applyBinaryOperationToRoot(operation);
        }
        else {
            this.applyBinaryOperationUnderParent(parent, operation);
        }
    }
    findParentOfNodeWithHigherPrecedence(precedence) {
        var _a;
        let currentNode = this.root;
        let currentParent = null;
        while (precedence > currentNode.precedence && currentNode !== currentParent) {
            currentParent = currentNode;
            currentNode = (_a = currentNode.children[currentNode.children.length - 1]) !== null && _a !== void 0 ? _a : currentNode;
        }
        return currentParent;
    }
    applyBinaryOperationToRoot(operation) {
        operation.leftOperand = this.root;
        this.root = operation;
    }
    applyBinaryOperationUnderParent(parent, operation) {
        if (parent instanceof binary_operation_1.BinaryOperation) {
            operation.leftOperand = parent.rightOperand;
            parent.rightOperand = operation;
        }
        else if (parent instanceof unary_operation_1.UnaryOperation) {
            operation.leftOperand = parent.operand;
            parent.operand = operation;
        }
        else {
            throw new Error(`Unknown parent type: ${parent}`);
        }
    }
    isEmpty() {
        return this.root instanceof no_op_1.NoOp;
    }
}
exports.CriteriaBuilder = CriteriaBuilder;
/**
 * @see CriteriaBuilder.where
 */
exports.where = CriteriaBuilder.where;
/**
 * @see CriteriaBuilder.not
 */
exports.not = CriteriaBuilder.not;
//# sourceMappingURL=criteria-builder.js.map